// Hand-rolled in-memory store with subscribe API. Per spec/tech-arch.md
// §5 + §11.7 + spec/build-plan.md P-020 row.
//
// **Why hand-rolled (no library).** Per OC §3.8 third-party-vs-hand-rolled
// check ran at PRD/Tech Arch lock — Tech Arch §5 explicitly chose hand-
// rolled at ~80 LOC. No re-evaluation needed. The Store contract is
// tiny (3 methods); a library (Zustand, Redux, Jotai) would add a
// dependency, a learning surface, and an upgrade-path obligation for
// no functional benefit at this scale.
//
// **Single global vs factory (D-082).** Both surfaces are exported:
// `createStore(initialState)` for tests + future migration scenarios that
// need an isolated Store; module-scope `store` as the single global the
// Preact hook (`useStore`) and the rest of the app consume. JSON import
// (P-034) swaps state via `store.setState(() => importedState)` — no
// factory rebuild needed.
//
// **Subscribe + setState semantics now consume `createSubscribable`**
// (P-035). The factory hoist fires at the 3rd consumer (Store +
// persistence-error + toast queue + lastSavedAt + pendingWrites);
// Store wraps the factory's `update` for the `setState(updater)` API
// shape that Tech Arch §11.7 contracts. Identity-skip no-op detection +
// Set-iterator-based listener fan-out preserved by the factory.

import { CURRENT_SCHEMA_VERSION, type AppState } from './schemas'
import { createSubscribable } from '../lib/subscribable'

export interface Store {
  getState(): AppState
  setState(updater: (s: AppState) => AppState): void
  subscribe(listener: (s: AppState) => void): () => void
}

/**
 * Initial app state used to bootstrap the global `store` instance.
 * P-021's persistence layer will overwrite this on boot if a saved
 * state is found in localStorage; for V1 init the empty arrays + null
 * selections + CURRENT_SCHEMA_VERSION are the canonical empty AppState.
 */
export const initialAppState: AppState = {
  templates: [],
  emails: [],
  currentEmailId: null,
  currentTemplateId: null,
  schemaVersion: CURRENT_SCHEMA_VERSION,
}

/**
 * Build a Store seeded with `initialState`. Exported for tests and any
 * future scenario needing an isolated store (e.g., P-021 migration test
 * fixtures). The app at runtime consumes the module-scope `store`
 * singleton below — not this factory.
 */
export function createStore(initialState: AppState): Store {
  const subscribable = createSubscribable<AppState>(initialState)
  return {
    getState: subscribable.getValue,
    setState: subscribable.update,
    subscribe: subscribable.subscribe,
  }
}

/**
 * Single global Store instance. Per Build-Plan P-020 decision surface +
 * D-082: single global for app simplicity. The `useStore()` Preact hook
 * (src/app/hooks/use-store.ts) subscribes to this; every screen that
 * needs app state reads through the hook, never importing `store`
 * directly inside a component (the hook owns the subscribe/cleanup).
 */
export const store: Store = createStore(initialAppState)
